import apiClient from '../axiosConfig';
import { components } from '../types/openapi';

// Tipos del OpenAPI
type CreateCaseFollowUp = components['schemas']['CreateCaseFollowUp'];
type CreateCaseQueryUSCIS = components['schemas']['CreateCaseQueryUSCIS'];
type UpdateCaseHistoryLawyer = components['schemas']['UpdateCaseHistoryLawyer'];
type CaseFollowUpResponse = components['schemas']['app__core__serializer__case_follow_up_lawyer__CaseFollowUpResponse'];
type CaseFollowUpUSCISResponse = components['schemas']['app__core__serializer__case_follow_up_lawyer__CaseFollowUpUSCISResponse'];
type CaseFollowUpRelationShipResponse = components['schemas']['CaseFollowUpRelationShipClientAndLawyersResponse'];
type CaseFollowUpWithLawyerResponse = components['schemas']['CaseFollowUpWithLawyerResponse'];
type CaseFollowUpAdminBase = components['schemas']['CaseFollowUpAdminBase'];
type CaseStatusHistoryUSCISResponse = components['schemas']['CaseStatusHistoryUSCISResponse'];
type PaginationCaseFollowUpUSCIS = components['schemas']['app__common__serializer__pagination__Pagination_CaseFollowUpUSCISResponse___2'];
type PaginationCaseFollowUpRelationShip = components['schemas']['Pagination_CaseFollowUpRelationShipClientAndLawyersResponse_'];
type PaginationCaseFollowUpWithLawyer = components['schemas']['Pagination_CaseFollowUpWithLawyerResponse_'];
type PaginationCaseFollowUp = components['schemas']['Pagination_CaseFollowUpResponse_'];
type PaginationCaseFollowUpAdmin = components['schemas']['Pagination_CaseFollowUpAdminBase_'];
type SearchCaseTypeEnum = components['schemas']['SearchCaseTypeEnum'];

/**
 * Servicio de seguimiento de casos
 * Maneja todas las operaciones relacionadas con el seguimiento de casos USCIS/EOIR/CEAC
 * Basado en los endpoints del OpenAPI
 */
class CaseFollowUpService {
  // ========== ENDPOINTS PARA ABOGADOS ==========
  
  /**
   * Crear alias de caso (Lawyer)
   * POST /api/core/case-follow-up-lawyer/lawyer/alias
   */
  async createLawyerAlias(data: CreateCaseFollowUp): Promise<CaseFollowUpResponse> {
    const response = await apiClient.post<CaseFollowUpResponse>(
      '/core/case-follow-up-lawyer/lawyer/alias',
      data
    );
    return response.data;
  }

  /**
   * Consultar caso USCIS (Lawyer)
   * POST /api/core/case-follow-up-lawyer/lawyer/query-uscis
   */
  async queryLawyerUSCIS(data: CreateCaseQueryUSCIS): Promise<CaseStatusHistoryUSCISResponse> {
    const response = await apiClient.post<CaseStatusHistoryUSCISResponse>(
      '/core/case-follow-up-lawyer/lawyer/query-uscis',
      data
    );
    return response.data;
  }

  /**
   * Agregar nota a caso (Lawyer)
   * PUT /api/core/case-follow-up-lawyer/lawyer/add-note
   */
  async addLawyerNote(data: UpdateCaseHistoryLawyer): Promise<CaseStatusHistoryUSCISResponse> {
    const response = await apiClient.put<CaseStatusHistoryUSCISResponse>(
      '/core/case-follow-up-lawyer/lawyer/add-note',
      data
    );
    return response.data;
  }

  /**
   * Obtener casos activos (Lawyer)
   * GET /api/core/case-follow-up-lawyer/lawyer/me-uscis-active
   */
  async getLawyerActiveCases(params?: {
    page?: number;
    limit?: number;
  }): Promise<PaginationCaseFollowUpUSCIS> {
    const response = await apiClient.get<PaginationCaseFollowUpUSCIS>(
      '/core/case-follow-up-lawyer/lawyer/me-uscis-active',
      { params }
    );
    return response.data;
  }

  /**
   * Obtener todos mis casos USCIS (Lawyer)
   * GET /api/core/case-follow-up-lawyer/lawyer/me-uscis
   */
  async getLawyerUSCISCases(params?: {
    page?: number;
    limit?: number;
  }): Promise<PaginationCaseFollowUpUSCIS> {
    const response = await apiClient.get<PaginationCaseFollowUpUSCIS>(
      '/core/case-follow-up-lawyer/lawyer/me-uscis',
      { params }
    );
    return response.data;
  }

  /**
   * Obtener todos los casos con cliente y abogado (Lawyer)
   * GET /api/core/case-follow-up-lawyer/lawyer/me-all
   */
  async getLawyerAllCases(params?: {
    page?: number;
    case_type?: SearchCaseTypeEnum;
    limit?: number;
  }): Promise<PaginationCaseFollowUpRelationShip> {
    const response = await apiClient.get<PaginationCaseFollowUpRelationShip>(
      '/core/case-follow-up-lawyer/lawyer/me-all',
      { params }
    );
    return response.data;
  }

  /**
   * Obtener caso por número USCIS (Lawyer)
   * GET /api/core/case-follow-up-lawyer/lawyer/me-uscis/{case_number}
   */
  async getLawyerCaseByNumber(caseNumber: string): Promise<CaseFollowUpUSCISResponse> {
    const response = await apiClient.get<CaseFollowUpUSCISResponse>(
      `/core/case-follow-up-lawyer/lawyer/me-uscis/${caseNumber}`
    );
    return response.data;
  }

  /**
   * Obtener sugerencias de caso (Lawyer)
   * GET /api/core/case-follow-up-lawyer/lawyer/suggestion-uscis/{case_number}
   */
  async getLawyerCaseSuggestions(caseNumber: string): Promise<string[]> {
    const response = await apiClient.get<string[]>(
      `/core/case-follow-up-lawyer/lawyer/suggestion-uscis/${caseNumber}`
    );
    return response.data;
  }

  // ========== ENDPOINTS PARA ADMIN ==========

  /**
   * Obtener casos de abogados (Admin) - Paginado
   * GET /api/core/case-follow-up-admin/lawyers/pagination
   */
  async getAdminLawyerCases(params?: {
    page?: number;
    case_type?: SearchCaseTypeEnum;
    lawyer?: number;
    limit?: number;
  }): Promise<PaginationCaseFollowUpWithLawyer> {
    const response = await apiClient.get<PaginationCaseFollowUpWithLawyer>(
      '/core/case-follow-up-admin/lawyers/pagination',
      { params }
    );
    return response.data;
  }

  /**
   * Obtener casos de clientes (Admin) - Paginado
   * GET /api/core/case-follow-up-admin/clients/pagination
   */
  async getAdminClientCases(params?: {
    page?: number;
    case_type?: SearchCaseTypeEnum;
    client?: number;
    limit?: number;
  }): Promise<PaginationCaseFollowUp> {
    const response = await apiClient.get<PaginationCaseFollowUp>(
      '/core/case-follow-up-admin/clients/pagination',
      { params }
    );
    return response.data;
  }

  /**
   * Obtener todos los casos (Admin) - Paginado
   * GET /api/core/case-follow-up-admin/admin/pagination
   */
  async getAdminAllCases(params?: {
    page?: number;
    case_type?: SearchCaseTypeEnum;
    client?: number;
    lawyer?: number;
    case_number?: string;
    limit?: number;
  }): Promise<PaginationCaseFollowUpAdmin> {
    const response = await apiClient.get<PaginationCaseFollowUpAdmin>(
      '/core/case-follow-up-admin/admin/pagination',
      { params }
    );
    return response.data;
  }
}

export default new CaseFollowUpService();

